import React from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { fmtMoney, fmtNumber, titleCase } from '../format';
import Icon from '../Icon';

export const PALETTE = ['#2563eb', '#f97316', '#10b981', '#8b5cf6', '#ef4444', '#0ea5e9', '#eab308', '#ec4899', '#14b8a6', '#64748b'];
const short = v => (Math.abs(v) >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : Math.abs(v) >= 1e3 ? `${(v / 1e3).toFixed(0)}k` : v);

// counts of rows by key, as the reference dashboards' "grouped" series
export const groupCount = (rows, keyOf, label = k => k) => {
  const m = new Map();
  rows.forEach(r => { const k = keyOf(r) || 'Unassigned'; m.set(k, (m.get(k) || 0) + 1); });
  return [...m.entries()].map(([k, value]) => ({ name: label(k), value })).sort((a, b) => b.value - a.value);
};

export function ChartCard({ chart, money }) {
  const data = (chart.data || []).map(d => ({ ...d, name: titleCase(d.name) }));
  const isMoney = money ?? data.some(d => d.value >= 10000);
  const fmt = v => (isMoney ? fmtMoney(v) : fmtNumber(v));
  return (
    <div className="panel chart-card">
      <h3 className="panel-title">{chart.title}</h3>
      {data.length === 0 ? <div className="empty">No data yet</div> : (
        <ResponsiveContainer width="100%" height={240}>
          {chart.type === 'pie' ? (
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2}>
                {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <Tooltip formatter={fmt} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          ) : (
            <BarChart data={data} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--line)" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={data.length > 5 ? -25 : 0} textAnchor={data.length > 5 ? 'end' : 'middle'} height={data.length > 5 ? 60 : 30} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={short} width={48} allowDecimals={false} />
              <Tooltip formatter={fmt} />
              <Bar dataKey="value" fill={PALETTE[0]} radius={[4, 4, 0, 0]} />
            </BarChart>
          )}
        </ResponsiveContainer>
      )}
    </div>
  );
}

// KPI cards; each: { label, value, tone, format: 'money'|undefined, icon, sub, onClick, selected }
export function KpiGrid({ kpis, className = '' }) {
  return (
    <div className={`kpi-grid ${className}`}>
      {kpis.map(k => {
        const Tag = k.onClick ? 'button' : 'div';
        return (
          <Tag key={k.label} type={k.onClick ? 'button' : undefined} onClick={k.onClick} className={`kpi tone-${k.tone || 'none'} ${k.selected ? 'selected' : ''}`}>
            <div className="kpi-head">
              <div className="kpi-label">{k.label}</div>
              <span className="kpi-icon">{k.format === 'money' ? '₦' : <Icon name={k.icon || 'hexagon'} size={17} strokeWidth={2.2} />}</span>
            </div>
            <div className="kpi-value">{k.format === 'money' ? fmtMoney(k.value) : typeof k.value === 'number' ? fmtNumber(k.value) : k.value}</div>
            {k.sub && <small className="kpi-sub">{k.sub}</small>}
          </Tag>
        );
      })}
    </div>
  );
}

// coloured counter tiles used across the employee workspaces (Employee Master, Attendance)
export function LcKpis({ items, className = 'em-kpis' }) {
  return (
    <div className={className}>
      {items.map(([label, value, color, icon, sub]) => (
        <div key={label} className={`em-kpi lc-${color}`}>
          <span className="lc-icon"><Icon name={icon} size={19} /></span>
          <div><b>{value}</b><span>{label}</span>{sub && <small>{sub}</small>}</div>
        </div>
      ))}
    </div>
  );
}
